import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useEndpointGuard } from '../../../../../../hooks/useEndpointGuard';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import * as streetsGuards from '../../../../../../utils/constants/auth/streets';
import { WithData } from '../../../../../../types/api';
import { PlacesStreetCollection } from '../../../../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../../../../utils/constants/misc';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import { PlaceCollection } from '../../../Dialogs/CreateEdit/CreateEditDialog.functions';
import { FormValues } from './EditIncompleteDialog.functions';
import ManageStreetsDialog from './ManageStreetsDialog';

function FormContainer(): JSX.Element {
  const { t } = useTranslation();

  const { setValue, watch } = useFormContext<FormValues>();
  const values = watch();
  const [isManageStreetsVisible, setIsManageStreetsVisible] =
    useState<boolean>(false);
  const canManageStreets = useEndpointGuard(streetsGuards.manageStreets);

  const { data: municipality, isLoading: isMunicipalityLoading } =
    useAxiosHook<PlaceCollection>(`/municipalities?limit=0`, {
      skipCache: true,
    });

  const {
    data: places,
    reload: reloadPlaces,
    isLoading: isPlacesLoading,
  } = useAxiosHook<any>(
    `/municipalities/${values?.municipalityId}/places?limit=0`,
    { skipWhen: !values?.municipalityId, skipCache: true }
  );

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${values?.placeId}/streets?limit=0`,
    {
      skipWhen: !values?.placeId,
      skipCache: true,
    }
  );

  const municipalityOptions = useMemo(
    () =>
      municipality && municipality?.data.length > 0
        ? municipality?.data.map((m: any) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [municipality]
  );

  const placesOptions = useMemo(
    () =>
      places && places?.length > 0
        ? places?.map((m: any) => ({
            label: m.place_name,
            value: m.place_id,
          }))
        : [],
    [places]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, values?.streetId]
  );

  return (
    <>
      <h3>{t('Edit Address')}</h3>
      <FieldWithErrors name="municipalityId" label={t('Municipality')}>
        <Controller
          name="municipalityId"
          render={({ field }) => (
            <Dropdown
              {...field}
              id="municipalityId"
              name="municipalityId"
              options={municipalityOptions}
              value={field.value}
              onChange={(e) => {
                if (e.value) {
                  console.log('e.target.name', e);
                  field.onChange(e.value);
                  const item = municipalityOptions?.find(
                    (s) => s.value === e.value
                  );
                  setValue('municipalityName', item?.label);

                  reloadPlaces({
                    url: `municipalities/${e.value}/places?limit=0`,
                  });
                } else {
                  setValue('municipalityId', null);
                  setValue('municipalityName', '');
                  setValue('placeId', null);
                  setValue('placeName', '');
                  setValue('streetId', null);
                  setValue('streetName', '');
                  setValue('housenumber', '');
                  setValue('entrance', '');
                  setValue('apartment', '');
                }
              }}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isMunicipalityLoading ? t('Loading...') : t('Search')
              }
              disabled={isMunicipalityLoading}
              className="data-cy-municipalityId"
              optionLabel="label"
              optionValue="value"
            />
          )}
        />
      </FieldWithErrors>
      <FieldWithErrors name="placeId" label={t('Place')}>
        <Controller
          name="placeId"
          render={({ field }) => (
            <Dropdown
              {...field}
              id="placeId"
              name="placeId"
              options={placesOptions}
              value={field.value}
              onChange={(e) => {
                if (e.value) {
                  console.log('e.target.name', e);
                  field.onChange(e.value);
                  const item = placesOptions?.find(
                    (s: any) => s.value === e.value
                  );
                  setValue('placeName', item?.label);

                  reloadStreets({
                    url: `places/${e.value}/streets?limit=0`,
                  });
                } else {
                  setValue('placeId', null);
                  setValue('placeName', '');
                  setValue('streetId', null);
                  setValue('streetName', '');
                  setValue('housenumber', '');
                  setValue('entrance', '');
                  setValue('apartment', '');
                }
              }}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isPlacesLoading ? t('Loading...') : t('Search places')
              }
              disabled={!values?.municipalityId || isPlacesLoading}
              className="data-cy-placeId"
              optionLabel="label"
              optionValue="value"
            />
          )}
        />
      </FieldWithErrors>

      {isStreetFreeInputAllowed ? (
        <FieldWithErrors name="rawName" label={t('Address')}>
          <Controller
            name="rawName"
            render={({ field }) => (
              <InputText
                id="rawName"
                name="rawName"
                value={field.value}
                onChange={field.onChange}
                disabled={!values?.placeName || isStreetsLoading}
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <>
          <FieldWithErrors name="streetId" label={t('Street')}>
            <div className="p-d-flex p-ai-center">
            <Controller
              name="streetId"
              render={({ field }) => (
                <Dropdown
                  id="streetId"
                  name="streetId"
                  options={streetsOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const item = streetsOptions?.find(
                        (s) => s.value === e.value
                      );
                      setValue('streetName', item?.label ?? '');
                    } else {
                      setValue('streetId', null);
                      setValue('streetName', '');
                      setValue('housenumber', '');
                      setValue('entrance', '');
                      setValue('apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={
                    isStreetsLoading ? t('Loading...') : t('Select Street')
                  }
                  disabled={!values?.placeId || isStreetsLoading}
                  className="data-cy-streetId"
                  style={{ flex: 1 }}
                />
              )}
            />
            <Button
              type="button"
              icon="pi pi-directions"
              label={t('Manage streets')}
              className="p-button-outlined p-ml-2"
              onClick={() => setIsManageStreetsVisible(true)}
              disabled={!values?.placeId || !canManageStreets}
              tooltip={t('Manage streets')}
            />
            </div>
          </FieldWithErrors>

          <ManageStreetsDialog
            visible={isManageStreetsVisible}
            onHide={() => setIsManageStreetsVisible(false)}
            placeId={values?.placeId}
            placeName={values?.placeName}
            onChanged={() =>
              reloadStreets({
                url: `places/${values?.placeId}/streets?limit=0`,
              })
            }
          />

          <div className="p-grid">
            <FieldWithErrors
              name="housenumber"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="housenumber"
                render={({ field }) => (
                  <InputText
                    id="housenumber"
                    name="housenumber"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.streetId}
                    placeholder={t('Street No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="entrance" label={false} className="p-col-4">
              <Controller
                name="entrance"
                render={({ field }) => (
                  <InputText
                    id="entrance"
                    name="entrance"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.streetId}
                    placeholder={t('Entrance No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="apartment" label={false} className="p-col-4">
              <Controller
                name="apartment"
                render={({ field }) => (
                  <InputText
                    id="apartment"
                    name="apartment"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values?.streetId}
                    placeholder={t('Flat No.')}
                  />
                )}
              />
            </FieldWithErrors>
          </div>
        </>
      )}
    </>
  );
}

export default FormContainer;
